import { resolveTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import type { DayOfWeek, Hour, HolidayHours } from "@/types"

const DAY_NAMES: DayOfWeek[] = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
]

const WEEKDAY_CODES: Record<string, DayOfWeek> = {
  Sun: "SUNDAY",
  Mon: "MONDAY",
  Tue: "TUESDAY",
  Wed: "WEDNESDAY",
  Thu: "THURSDAY",
  Fri: "FRIDAY",
  Sat: "SATURDAY",
}

const DAY_START = "00:00:00"
const DAY_END = "23:59:59"
const DAY_SECONDS = 86_400

/**
 * Sunday of a week with no DST transition in any zone we serve, so a weekday
 * name can be turned into a real instant and back. Mirrors `referenceWeek` in
 * merchant-service-gateway's `schedule/hours.go`.
 */
const REFERENCE_SUNDAY_UTC = Date.UTC(2026, 8, 6)

/** One weekly window already on the tenant's wall clock, as "HH:MM:SS". */
export type LocalWindow = {
  day: DayOfWeek
  startTime: string
  endTime: string
}

/** One dated window (a pause) already on the tenant's wall clock. */
export type LocalDatedWindow = {
  date: string
  startTime: string
  endTime: string
}

/** The tenant's own clock at an instant: calendar day, weekday and "HH:MM". */
export type TenantClock = {
  date: string
  day: DayOfWeek
  time: string
}

/**
 * Whether a wire bound is a UTC time-of-day (`HH:MM:SS.000Z`) instead of the
 * legacy wall-clock `HH:MM`. The `Z` suffix is the only discriminator the OD
 * payload gives, and it is what lets a consumer accept both during the rollout.
 */
export function isUtcTimeOfDay(bound: string | null | undefined): boolean {
  return typeof bound === "string" && /z\s*$/i.test(bound)
}

function parseSeconds(raw: string | null | undefined): number | null {
  if (typeof raw !== "string") return null
  const match = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*z?\s*$/i.exec(raw)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = Number(match[3] ?? "0")
  if (hours > 24 || minutes > 59 || seconds > 59) return null
  return hours * 3600 + minutes * 60 + seconds
}

function formatSeconds(total: number): string {
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":")
}

function padBound(raw: string): string {
  const seconds = parseSeconds(raw)
  return seconds === null ? raw : formatSeconds(seconds)
}

function zonedParts(instant: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(instant))

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ""

  const hour = value("hour") === "24" ? "00" : value("hour")

  return {
    date: `${value("year")}-${value("month")}-${value("day")}`,
    day: WEEKDAY_CODES[value("weekday")] ?? "SUNDAY",
    time: `${hour}:${value("minute")}:${value("second")}`,
  }
}

/** The tenant's calendar day, weekday and "HH:MM" at an instant. */
export function tenantClock(now: Date = new Date(), timeZone?: string | null): TenantClock {
  const zoned = zonedParts(now.getTime(), resolveTenantTimezone(timeZone))
  return { date: zoned.date, day: zoned.day, time: zoned.time.slice(0, 5) }
}

/** Tenant weekday and "HH:MM:SS" at an instant — the precision loyalty windows use. */
export function tenantWallClock(
  now: Date = new Date(),
  timeZone?: string | null,
): { day: DayOfWeek; time: string } {
  const zoned = zonedParts(now.getTime(), resolveTenantTimezone(timeZone))
  return { day: zoned.day, time: zoned.time }
}

function dayIndex(day: DayOfWeek): number {
  return DAY_NAMES.indexOf(day)
}

function shiftWeekly(day: DayOfWeek, start: number, end: number, timeZone: string): LocalWindow[] {
  const base = REFERENCE_SUNDAY_UTC + dayIndex(day) * DAY_SECONDS * 1000
  const startAt = zonedParts(base + start * 1000, timeZone)
  const endAt = zonedParts(base + end * 1000, timeZone)

  if (startAt.date === endAt.date) {
    return [{ day: startAt.day, startTime: startAt.time, endTime: endAt.time }]
  }

  return [
    { day: startAt.day, startTime: startAt.time, endTime: DAY_END },
    { day: endAt.day, startTime: DAY_START, endTime: endAt.time },
  ]
}

/**
 * Folds windows that touch second-to-second on the same day back into one, so
 * the two halves the UTC midnight split produced read as the single window the
 * merchant actually authored.
 */
function mergeContiguous(windows: LocalWindow[]): LocalWindow[] {
  const sorted = [...windows].sort((a, b) => {
    const byDay = dayIndex(a.day) - dayIndex(b.day)
    if (byDay !== 0) return byDay
    return a.startTime.localeCompare(b.startTime)
  })

  const merged: LocalWindow[] = []
  for (const window of sorted) {
    const previous = merged[merged.length - 1]
    const previousEnd = previous ? parseSeconds(previous.endTime) : null
    const start = parseSeconds(window.startTime)

    if (
      previous &&
      previous.day === window.day &&
      previousEnd !== null &&
      start !== null &&
      previousEnd + 1 === start
    ) {
      merged[merged.length - 1] = { ...previous, endTime: window.endTime }
      continue
    }
    merged.push(window)
  }
  return merged
}

/**
 * Weekly windows on the tenant's wall clock, whatever the wire sent.
 *
 * A payload whose bounds carry the `Z` suffix is a UTC time-of-day already
 * split on the UTC midnight (`FRIDAY 23:00:00.000Z` + `SATURDAY
 * 00:00:00.000Z`); it is converted to the tenant zone and the two halves are
 * rejoined into the one local window they always were. A legacy `HH:MM`
 * payload is taken as the tenant's clock and only padded to `HH:MM:SS`.
 */
export function normalizeWeekHours(
  hours: Hour[] | null | undefined,
  timeZone?: string | null,
): LocalWindow[] {
  if (!hours?.length) return []
  const zone = resolveTenantTimezone(timeZone)
  const windows: LocalWindow[] = []

  for (const hour of hours) {
    const { startTime, endTime } = hour.timePeriods ?? {}
    const utc = isUtcTimeOfDay(startTime) || isUtcTimeOfDay(endTime)
    const start = parseSeconds(startTime)
    const end = parseSeconds(endTime)

    for (const day of hour.dayOfWeek ?? []) {
      if (!utc || start === null || end === null || dayIndex(day) < 0) {
        windows.push({ day, startTime: padBound(startTime), endTime: padBound(endTime) })
        continue
      }
      windows.push(...shiftWeekly(day, start, end, zone))
    }
  }

  return mergeContiguous(windows)
}

function calendarDate(raw: unknown): string {
  if (typeof raw === "string") return raw.slice(0, 10)
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return [
      raw.getFullYear(),
      String(raw.getMonth() + 1).padStart(2, "0"),
      String(raw.getDate()).padStart(2, "0"),
    ].join("-")
  }
  return String(raw).slice(0, 10)
}

/**
 * Dated pause windows on the tenant's wall clock. A UTC window that crosses
 * local midnight lands on the two calendar days it really covers; a missing or
 * unparseable window still means the whole day, never "no pause".
 */
export function normalizeHolidayHours(
  holidayHours: HolidayHours | null | undefined,
  timeZone?: string | null,
): LocalDatedWindow[] {
  if (!holidayHours?.length) return []
  const zone = resolveTenantTimezone(timeZone)
  const windows: LocalDatedWindow[] = []

  for (const holiday of holidayHours) {
    const date = calendarDate(holiday.date)
    const { startTime, endTime } = holiday.timePeriods ?? {}
    const start = parseSeconds(startTime)
    const end = parseSeconds(endTime)

    if (start === null || end === null) {
      windows.push({ date, startTime: DAY_START, endTime: DAY_END })
      continue
    }

    if (!isUtcTimeOfDay(startTime) && !isUtcTimeOfDay(endTime)) {
      windows.push({ date, startTime: formatSeconds(start), endTime: formatSeconds(end) })
      continue
    }

    const base = Date.parse(`${date}T00:00:00.000Z`)
    if (Number.isNaN(base)) {
      windows.push({ date, startTime: DAY_START, endTime: DAY_END })
      continue
    }

    const startAt = zonedParts(base + start * 1000, zone)
    const endAt = zonedParts(base + end * 1000, zone)
    if (startAt.date === endAt.date) {
      windows.push({ date: startAt.date, startTime: startAt.time, endTime: endAt.time })
      continue
    }
    windows.push({ date: startAt.date, startTime: startAt.time, endTime: DAY_END })
    windows.push({ date: endAt.date, startTime: DAY_START, endTime: endAt.time })
  }

  return windows
}

/** Whether a "HH:MM" clock falls inside a normalized window, inclusive. */
export function coversTime(window: { startTime: string; endTime: string }, hhmm: string): boolean {
  return window.startTime.slice(0, 5) <= hhmm && window.endTime.slice(0, 5) >= hhmm
}
